import { KeyResponse } from "./api/secure";

const KMS_BASE = "https://cloudkms.googleapis.com";

export type KeyID = `${string}:${string}`;

export interface SecurePayload<D = unknown, T = unknown> {
  id: KeyID;
  key: T;
  data: T;
  iv: T;
}

export type PrivateData<S extends SecurePayload<any, any>> =
  S extends SecurePayload<infer D, any> ? D : never;

export async function encryptJsonObject({
  jsonObject,
  publicKeyPem,
  subtle,
}: {
  jsonObject: object;
  publicKeyPem: string;
  subtle?: (typeof globalThis)["crypto"]["subtle"];
}) {
  subtle = subtle ?? globalThis.crypto.subtle;
  try {
    // Step 1: Serialize the JSON object to a string
    const jsonString = JSON.stringify(jsonObject);

    // Step 2: Generate a symmetric key (AES-GCM)
    const symmetricKey = await subtle.generateKey(
      { name: "AES-GCM", length: 256 },
      true,
      ["encrypt", "decrypt"],
    );

    // Step 3: Encrypt the JSON string with the symmetric key
    const encoder = new TextEncoder();
    const iv = globalThis.crypto.getRandomValues(new Uint8Array(12)); // Initialization vector
    const encryptedDataBuffer = await subtle.encrypt(
      { name: "AES-GCM", iv: iv },
      symmetricKey,
      encoder.encode(jsonString),
    );

    // Convert encrypted data from ArrayBuffer to Uint8Array
    const encryptedData = new Uint8Array(encryptedDataBuffer);

    // Step 4: Import the public key (from PEM format)
    const publicKey = await importPublicKey(publicKeyPem);

    // Step 5: Export the symmetric key and encrypt it with the public key (RSA-OAEP)
    const symmetricKeyRaw = await subtle.exportKey("raw", symmetricKey);
    const encryptedSymmetricKeyBuffer = await subtle.encrypt(
      { name: "RSA-OAEP" },
      publicKey,
      symmetricKeyRaw,
    );

    // Convert encrypted symmetric key from ArrayBuffer to Uint8Array
    const encryptedSymmetricKey = new Uint8Array(encryptedSymmetricKeyBuffer);

    // Step 6: Return the encrypted data and encrypted symmetric key
    return {
      encryptedData,
      encryptedSymmetricKey,
      iv, // Include the IV for decryption later
    };
  } catch (error) {
    console.error("Encryption failed:", error);
    throw error;
  }
}

export async function decryptJsonObject<D = object>({
  encryptedData,
  decryptedSymmetricKey,
  iv,
  subtle,
}: {
  encryptedData: Uint8Array;
  decryptedSymmetricKey: Uint8Array;
  iv: Uint8Array;
  subtle?: (typeof globalThis)["crypto"]["subtle"];
}): Promise<D> {
  subtle = subtle ?? globalThis.crypto.subtle;
  try {
    // Import the symmetric key from raw bytes
    const cryptoKey = await subtle.importKey(
      "raw",
      decryptedSymmetricKey as BufferSource,
      { name: "AES-GCM" },
      true,
      ["decrypt"],
    );

    // Step 1: Decrypt the encrypted data using the decrypted symmetric key
    const decryptedBuffer = await subtle.decrypt(
      { name: "AES-GCM", iv: iv as BufferSource },
      cryptoKey,
      encryptedData as BufferSource,
    );

    // Step 2: Convert the decrypted ArrayBuffer to a string
    const decoder = new TextDecoder();
    const jsonString = decoder.decode(decryptedBuffer);

    // Step 3: Parse the JSON string back into an object
    return JSON.parse(jsonString);
  } catch (error) {
    console.error("Decryption failed:", error);
    throw error;
  }
}

// Helper function to import a PEM-formatted RSA public key
async function importPublicKey(pem: string) {
  // Remove PEM formatting and ensure it's clean
  const pemHeader = "-----BEGIN PUBLIC KEY-----";
  const pemFooter = "-----END PUBLIC KEY-----";
  const pemContents = pem
    .replace(pemHeader, "")
    .replace(pemFooter, "")
    .replace(/\n/g, "")
    .replace(/\r/g, "")
    .trim();

  const binaryDerString = globalThis.atob(pemContents);
  const binaryDer = new Uint8Array(binaryDerString.length);
  for (let i = 0; i < binaryDerString.length; i++) {
    binaryDer[i] = binaryDerString.charCodeAt(i);
  }

  // Import the public key
  return await globalThis.crypto.subtle.importKey(
    "spki",
    binaryDer.buffer,
    { name: "RSA-OAEP", hash: "SHA-256" },
    true,
    ["encrypt"],
  );
}

export const DEFAULT_PRODUCTION_PUBLIC_KEY: KeyResponse = {
  id: "default:1",
  key: `-----BEGIN PUBLIC KEY-----
MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAxRrlOJwPm8y6KhnBFtKC
kLdYkLSuO56nAUfoSIHe4S1aLUISp83v1seY4WT77IQWe1E08qLdNL5nbaqWeEgQ
/4z7Jfsa5V2muTp4goaKRdSprJBl7OZG7V//0oB8ps0qdMWY4qXI/RFSLu7zFufO
xSJaUci5ZIEU2XOE4H9WjYl/P6KQZ+A7jivmCBtlxLKP8re4RTW97D25+9LftoSn
8ERMYVsAGsDza/B8+h5Xd+dvrUIal1N31xrLSvphqpaddfBO0vn8RJcXqRcFnPuQ
O+HgHSD5yyKBxY9h0LN2aLbbGbG68M7M2jqGx1tV/A+vPHwLzCZfZ94SFK/TJan9
YQIDAQAB
-----END PUBLIC KEY-----`,
};

export const DEFAULT_STAGING_PUBLIC_KEY: KeyResponse = {
  id: "default:1",
  key: `-----BEGIN PUBLIC KEY-----
MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEA0OQSEQa0CSYWOtH7gr63
ptxwTPeh0EVpy5aF75sH+2wuamgU4D7m93pAQ7fVpuZX9S1oRlvxOCkEKVNYzvwo
TFSGAhUNX6GpC78SDKiFow266llGP+PtSWjOaFTK8MJviLn+Qc2oLzItmKJBI9Wg
pHZkdgwaEUbLUZoZDYRY9glf6/mD878s9ti4Xrfs+CIy4G8UTBQJ30BmePPhmSSY
ff2I1P81uN1aGtq6gk7+zuLhylWa+iHLlYt49tgfp8q4f5a7j2xcZzRzbRlCsgIc
i05PqTKKnU0Gh8gfzp+WQYdbDOqglyQy6XSDhexSnryOn5fsJk994WKV3+S/uMPw
nwIDAQAB
-----END PUBLIC KEY-----`,
};

/**
 * Converts a key ID (in format "name:version") to a full Google Cloud KMS key path
 */
export const getKmsKeyPath = ({
  id,
  projectId,
}: {
  id: `${string}:${string}`;
  projectId: string;
}) => {
  const [name, version] = id.split(":");
  return [
    "projects",
    projectId,
    "locations",
    "global",
    "keyRings",
    "set",
    "cryptoKeys",
    name,
    "cryptoKeyVersions",
    version,
  ].join("/");
};

/**
 *  decrypts the symmetric key used to encode payload
 */
const kmsDecrypt = async ({
  encryptedSymmetricKey,
  kmsAccessKey,
  kmsKeyPath,
}: {
  encryptedSymmetricKey: string;
  kmsAccessKey: string;
  kmsKeyPath: string;
}): Promise<string | undefined> => {
  try {
    // Use :asymmetricDecrypt for asymmetric keys
    const url = new URL(`/v1/${kmsKeyPath}:asymmetricDecrypt`, KMS_BASE);

    const response = await fetch(url.toString(), {
      method: "POST",
      headers: {
        Authorization: `Bearer ${kmsAccessKey}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ ciphertext: encryptedSymmetricKey }),
    });

    if (!response.ok) {
      const errorBody = await response.text();
      console.error(
        `KMS decryption failed with status ${response.status} ${response.statusText}:`,
        errorBody,
      );
      return undefined;
    }

    const data = await response.json();
    if (data && data.plaintext) {
      return atob(data.plaintext);
    } else {
      console.error("KMS decryption response did not contain plaintext.", data);
      return undefined;
    }
  } catch (err) {
    console.error("Error during KMS decryption request or processing:", err);
    return undefined;
  }
};

export async function decryptData<D = unknown>(
  encryptedData: Uint8Array,
  encryptedSymmetricKey: Uint8Array,
  iv: Uint8Array,
  kmsKeyPath: string,
  kmsAccessKey: string,
): Promise<D | undefined> {
  let base64EncryptedSymmetricKey: string;
  try {
    let binaryString = "";
    for (let i = 0; i < encryptedSymmetricKey.length; i++) {
      binaryString += String.fromCharCode(encryptedSymmetricKey[i]);
    }
    base64EncryptedSymmetricKey = btoa(binaryString);
  } catch (e) {
    console.error(
      "Failed to convert symmetric key to base64 for KMS request:",
      e,
    );
    return undefined;
  }

  const decryptedSymmetricKeyRawString = await kmsDecrypt({
    kmsAccessKey,
    kmsKeyPath,
    encryptedSymmetricKey: base64EncryptedSymmetricKey,
  });

  if (!decryptedSymmetricKeyRawString) {
    console.error(
      "Decryption failed: Symmetric key could not be decrypted by KMS.",
    );
    return undefined;
  }

  let decryptedSymmetricKeyBytes: Uint8Array;
  try {
    // Convert the raw string (decoded from base64 by atob in kmsDecrypt) into Uint8Array
    // by taking the char code of each character in the raw string.
    const L = decryptedSymmetricKeyRawString.length;
    decryptedSymmetricKeyBytes = new Uint8Array(L);
    for (let i = 0; i < L; i++) {
      decryptedSymmetricKeyBytes[i] =
        decryptedSymmetricKeyRawString.charCodeAt(i);
    }
  } catch (e) {
    console.error(
      "Failed to convert decrypted symmetric key string to Uint8Array:",
      e,
    );
    return undefined;
  }

  return decryptJsonObject<D>({
    encryptedData,
    decryptedSymmetricKey: decryptedSymmetricKeyBytes,
    iv,
  });
}

const getUint8Array = (data: unknown) => {
  if (data instanceof Uint8Array) {
    return data;
  }

  if (
    typeof data === "object" &&
    data != null &&
    "toUint8Array" in data &&
    typeof data.toUint8Array === "function"
  ) {
    const u = data.toUint8Array();
    if (u instanceof Uint8Array) {
      return u;
    }
  }

  throw new Error("Data is not in a format we know how to handle");
};

/**
 * Attempts to decrypt the provided secure payload.  Will throw an error if the
 * payload is invalid or is not valid JSON.
 */
export const decryptSecurePayload = async <D = unknown, T = unknown>(
  payload: SecurePayload<D, T>,
  kmsAccessKey: string,
  projectId: string,
): Promise<D | undefined> => {
  const data = getUint8Array(payload.data);
  const key = getUint8Array(payload.key);
  const iv = getUint8Array(payload.iv);

  return decryptData<D>(
    data,
    key,
    iv,
    getKmsKeyPath({ projectId, ...payload }),
    kmsAccessKey,
  );
};
